import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import type { VideoItem } from "@/types/video";
import { episodeRanges, regions, years, sortOptions } from "@/mocks/filters";
import VideoCard from "@/components/base/VideoCard";
import Pagination from "@/components/base/Pagination";

interface CategoryBrowserProps {
  title: string;
  subtitle: string;
  categoryLabel: string;
  categoryLink: string;
  items: VideoItem[];
  genreOptions: string[];
  showEpisodeFilter?: boolean;
}

const PAGE_SIZE = 12;

interface FilterGroupProps {
  label: string;
  options: string[];
  value: string;
  onChange: (value: string) => void;
}

function FilterGroup({ label, options, value, onChange }: FilterGroupProps) {
  return (
    <div className="py-3.5 border-b border-background-200 last:border-b-0">
      <p className="text-xs font-label font-semibold text-foreground-400 mb-2.5">{label}</p>
      <div className="flex flex-wrap gap-1.5">
        {options.map((option) => (
          <button
            key={option}
            type="button"
            onClick={() => onChange(option)}
            className={`px-2.5 py-1 rounded-md text-xs font-label transition-colors cursor-pointer whitespace-nowrap ${
              value === option
                ? "bg-primary-500 text-background-50"
                : "bg-background-100 text-foreground-600 hover:bg-background-200"
            }`}
          >
            {option}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function CategoryBrowser({
  title,
  subtitle,
  categoryLabel,
  categoryLink,
  items,
  genreOptions,
  showEpisodeFilter = false,
}: CategoryBrowserProps) {
  const [genre, setGenre] = useState("全部");
  const [region, setRegion] = useState("全部");
  const [year, setYear] = useState("全部");
  const [episodes, setEpisodes] = useState("全部");
  const [sort, setSort] = useState("updated");
  const [page, setPage] = useState(1);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const handleFilter = (setter: (v: string) => void) => (value: string) => {
    setter(value);
    setPage(1);
  };

  const filtered = useMemo(() => {
    const result = items.filter((item) => {
      if (genre !== "全部" && !item.genres.includes(genre)) return false;
      if (region !== "全部" && item.region !== region) return false;
      if (year !== "全部" && String(item.year) !== year) return false;
      if (showEpisodeFilter && episodes !== "全部") {
        const count = item.episodes ?? 0;
        if (episodes === "12集以下" && count > 12) return false;
        if (episodes === "12-24集" && (count <= 12 || count > 24)) return false;
        if (episodes === "25-40集" && (count <= 24 || count > 40)) return false;
        if (episodes === "40集以上" && count <= 40) return false;
      }
      return true;
    });

    result.sort((a, b) => {
      if (sort === "hot") return b.playCount - a.playCount;
      if (sort === "rating") return b.rating - a.rating;
      return a.updatedAt < b.updatedAt ? 1 : -1;
    });

    return result;
  }, [items, genre, region, year, episodes, sort, showEpisodeFilter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageItems = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const resetFilters = () => {
    setGenre("全部");
    setRegion("全部");
    setYear("全部");
    setEpisodes("全部");
    setPage(1);
  };

  const sidebar = (
    <div className="rounded-lg bg-background-50 border border-background-200 px-4">
      <FilterGroup label="类型" options={genreOptions} value={genre} onChange={handleFilter(setGenre)} />
      <FilterGroup label="地区" options={regions} value={region} onChange={handleFilter(setRegion)} />
      <FilterGroup label="年份" options={years} value={year} onChange={handleFilter(setYear)} />
      {showEpisodeFilter && (
        <FilterGroup label="集数范围" options={episodeRanges} value={episodes} onChange={handleFilter(setEpisodes)} />
      )}
    </div>
  );

  return (
    <div className="w-full px-4 md:px-6 py-6 md:py-8">
      <nav className="flex items-center gap-1.5 text-sm text-foreground-500 mb-4" aria-label="面包屑导航">
        <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer">
          首页
        </Link>
        <i className="ri-arrow-right-s-line"></i>
        <span className="text-foreground-800 font-medium">{categoryLabel}</span>
      </nav>

      <div className="mb-6">
        <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">{title}</h1>
        <p className="mt-2 text-sm text-foreground-500">{subtitle}</p>
      </div>

      <div className="flex flex-col lg:flex-row gap-6">
        <aside className="w-full lg:w-60 shrink-0">
          <button
            type="button"
            onClick={() => setFiltersOpen((v) => !v)}
            className="lg:hidden w-full flex items-center justify-between px-4 py-3 rounded-lg bg-background-50 border border-background-200 text-sm font-label text-foreground-700 cursor-pointer"
          >
            <span className="flex items-center gap-2">
              <i className="ri-filter-3-line"></i>
              筛选条件
            </span>
            <i className={filtersOpen ? "ri-arrow-up-s-line text-lg" : "ri-arrow-down-s-line text-lg"}></i>
          </button>
          <div className={`${filtersOpen ? "block" : "hidden"} lg:block lg:sticky lg:top-20 mt-4 lg:mt-0`}>
            {sidebar}
            <button
              type="button"
              onClick={resetFilters}
              className="mt-3 w-full py-2.5 rounded-lg border border-background-200 text-sm font-label text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer"
            >
              重置筛选
            </button>
          </div>
        </aside>

        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
            <p className="text-sm text-foreground-500">
              共 <span className="font-semibold text-primary-600">{filtered.length}</span> 部作品
            </p>
            <div className="flex items-center gap-1.5 p-1 rounded-full bg-background-100">
              {sortOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => {
                    setSort(option.value);
                    setPage(1);
                  }}
                  className={`px-3 py-1.5 rounded-full text-xs font-label transition-colors cursor-pointer whitespace-nowrap ${
                    sort === option.value
                      ? "bg-background-50 text-primary-600 font-semibold shadow-sm"
                      : "text-foreground-500 hover:text-foreground-800"
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          {pageItems.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-5">
              {pageItems.map((item) => (
                <VideoCard key={item.id} item={item} showPlayCount />
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-20 text-center rounded-lg bg-background-100">
              <span className="w-16 h-16 rounded-full bg-background-200 text-foreground-400 flex items-center justify-center">
                <i className="ri-emotion-sad-line text-3xl"></i>
              </span>
              <p className="mt-4 text-foreground-700 font-medium">没有符合筛选条件的作品</p>
              <p className="mt-1 text-sm text-foreground-500">试试调整筛选项，或重置后重新浏览</p>
              <button
                type="button"
                onClick={resetFilters}
                className="mt-5 px-5 py-2.5 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-label font-semibold transition-colors cursor-pointer whitespace-nowrap"
              >
                重置筛选条件
              </button>
            </div>
          )}

          <Pagination current={currentPage} total={totalPages} onChange={setPage} />
        </div>
      </div>
    </div>
  );
}